/**
 * /admin/ad-campaigns/:cid — Analytics for one paid campaign.
 *
 * Charts:
 *   1. 7-day hourly impressions + clicks area chart (Recharts).
 *   2. Today's cap burn-down horizontal bar.
 *   3. Mini funnel: Served → Clicked → CTR %.
 *   4. Breakdown tables by sister portal and by placement.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, Loader2, Target, Globe2, TrendingUp, MousePointerClick,
  Clock,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";


export default function AdminAdCampaignAnalytics() {
  const { cid } = useParams();
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !isAdmin) return;
    (async () => {
      try {
        const r = await http.get(`/ads/admin/campaigns/${cid}/analytics`);
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [cid, user, isAdmin]);

  if (!user) return <Guard><Link to="/login" className="underline">Sign in to continue</Link></Guard>;
  if (!isAdmin) return <Guard>Admin access required.</Guard>;
  if (loading) return <Guard><Loader2 className="w-5 h-5 animate-spin" /></Guard>;
  if (!data) return <Guard>Campaign not found.</Guard>;

  const { campaign, series_hourly_168h, totals_7d, today, by_portal, by_placement, flight } = data;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-campaign-analytics">
      <SocialPulseHeader />
      <main className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
        <Link to="/admin/ad-campaigns" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back to campaigns
        </Link>
        <h1 className="font-serif text-3xl font-black text-slate-900">
          {campaign.name}
        </h1>
        <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
          <StatusChip s={campaign.status} />
          <span>priority {campaign.priority}</span>
          {campaign.daily_cap && <span>· daily cap {campaign.daily_cap.toLocaleString()}</span>}
          <span>· {flight?.days_left ?? 0}d left</span>
        </div>

        {/* ── KPI row ───────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Kpi icon={TrendingUp} label="Impressions · 7d" value={totals_7d.impressions} tone="violet" />
          <Kpi icon={MousePointerClick} label="Clicks · 7d" value={totals_7d.clicks} tone="amber" />
          <Kpi icon={TrendingUp} label="CTR · 7d" value={`${totals_7d.ctr}%`} tone="emerald" />
          <Kpi icon={Clock}
               label={today.cap ? `Today · cap ${today.cap_pct ?? 0}%` : "Today · impressions"}
               value={today.impressions} tone="blue" />
        </div>

        {/* ── Area chart ────────────────────────────────────── */}
        <section className="mt-6 bg-white rounded-2xl ring-1 ring-slate-200 p-4" data-testid="admin-campaign-chart">
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif text-lg font-bold">Impressions &amp; clicks · trailing 7 days</div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={series_hourly_168h} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="imp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="clk" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={23}
                       tickFormatter={(h) => new Date(h).toLocaleDateString(undefined, { month: "short", day: "numeric" })} />
                <YAxis tick={{ fontSize: 10 }} width={32} />
                <Tooltip
                  formatter={(v, n) => [v.toLocaleString(), n]}
                  labelFormatter={(h) => new Date(h).toLocaleString()}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Area type="monotone" dataKey="impressions" stroke="#2563eb"
                      fill="url(#imp)" strokeWidth={2} />
                <Area type="monotone" dataKey="clicks" stroke="#f59e0b"
                      fill="url(#clk)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* ── Cap burn-down + funnel ────────────────────────── */}
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-4">
            <div className="font-serif text-lg font-bold mb-2">Today's cap burn-down</div>
            {today.cap ? (
              <>
                <div className="h-5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${today.cap_pct >= 100 ? "bg-amber-500" : "bg-blue-600"}`}
                    style={{ width: `${Math.min(100, today.cap_pct || 0)}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  <strong className="text-slate-900">{today.impressions.toLocaleString()}</strong>
                  {" "}of {today.cap.toLocaleString()} ({today.cap_pct ?? 0}%) ·
                  {today.cap_pct >= 100 ? " cap reached, serving falls back to house ads" : " on pace"}
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-500">
                No daily cap on this campaign — serves until the budget or dates run out.
              </div>
            )}
          </section>

          <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-4">
            <div className="font-serif text-lg font-bold mb-2">Click funnel · 7d</div>
            <FunnelBar label="Impressions" value={totals_7d.impressions} max={totals_7d.impressions || 1} color="bg-violet-500" />
            <FunnelBar label="Clicks"      value={totals_7d.clicks}      max={totals_7d.impressions || 1} color="bg-amber-500" />
            <div className="text-xs text-slate-500 mt-1">
              CTR <strong className="text-slate-900">{totals_7d.ctr}%</strong>
              {totals_7d.ctr >= 1 ? " · healthy for display" : " · consider stronger CTA"}
            </div>
          </section>
        </div>

        {/* ── Breakdowns ────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <BreakdownTable title="By sister portal" rows={by_portal} keyField="portal" icon={Globe2} />
          <BreakdownTable title="By placement"      rows={by_placement} keyField="placement" icon={Target} />
        </div>
      </main>
      <SocialPulseFooter />
    </div>
  );
}


function Guard({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-slate-600">{children}</div>;
}


function Kpi({ icon: Icon, label, value, tone }) {
  const tones = {
    violet:  "bg-violet-50 ring-violet-200 text-violet-700",
    amber:   "bg-amber-50 ring-amber-200 text-amber-700",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-700",
    blue:    "bg-blue-50 ring-blue-200 text-blue-700",
  };
  const t = tones[tone] || tones.violet;
  return (
    <div className={`rounded-xl ring-1 ${t} p-3`} data-testid={`admin-campaign-kpi-${tone}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-serif text-2xl font-black text-slate-900 mt-1">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}


function FunnelBar({ label, value, max, color }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-0.5">
        <span>{label}</span>
        <span className="font-mono text-slate-900 font-semibold">{value.toLocaleString()}</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


function BreakdownTable({ title, rows, keyField, icon: Icon }) {
  return (
    <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-4">
      <div className="font-serif text-lg font-bold mb-2 flex items-center gap-1">
        <Icon className="w-4 h-4 text-slate-500" /> {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">No data in the last 7 days.</div>
      ) : (
        <table className="w-full text-xs" data-testid={`admin-campaign-table-${keyField}`}>
          <thead>
            <tr className="text-slate-500">
              <th className="text-left pb-1 font-semibold">{keyField === "portal" ? "Portal" : "Placement"}</th>
              <th className="text-right pb-1 font-semibold">Impressions</th>
              <th className="text-right pb-1 font-semibold">Clicks</th>
              <th className="text-right pb-1 font-semibold">CTR</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[keyField]} className="border-t border-slate-100">
                <td className="py-1 font-semibold text-slate-800">{r[keyField]}</td>
                <td className="py-1 text-right">{r.impressions.toLocaleString()}</td>
                <td className="py-1 text-right">{r.clicks.toLocaleString()}</td>
                <td className="py-1 text-right text-slate-500">{r.ctr}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}


function StatusChip({ s }) {
  const map = {
    active:  "bg-emerald-100 text-emerald-700",
    paused:  "bg-amber-100 text-amber-700",
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[s] || "bg-slate-100 text-slate-600"}`}>{s.toUpperCase()}</span>;
}
